import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogUrl } from '@/utils/urls';
import { getItemModelCatalogId, getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { modalTypes } from '@/enums';
import { openBuyNowModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useTrackBuyNowPressedAnalytics } from '@/utils/amplitude/analytics';

type Props = {
    itemId: number;
};

export const useBuyNowCallbacks = ({ itemId }: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const verifyLogin = useRequireLogin();

    const { buyNowPrice } = useAppSelector((state) => getItemSummary(state, itemId));
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));

    const trackBuyNowPressedAnalytics = useTrackBuyNowPressedAnalytics(itemId);

    const onBuyNowClicked = useCallback(async () => {
        await verifyLogin({
            requireCompleteProfile: true,
            requireLogin: true,
            requireVerifiedAddress: true,
            runFunction: () => {
                trackBuyNowPressedAnalytics(false);
                dispatch(
                    openBuyNowModal({
                        itemId,
                        modalType: modalTypes.BUY_NOW,
                        offerPrice: buyNowPrice,
                    })
                );
            },
        });
    }, [verifyLogin, trackBuyNowPressedAnalytics, dispatch, itemId, buyNowPrice]);

    const onMakeAnOfferClicked = useCallback(async () => {
        await verifyLogin({
            requireCompleteProfile: true,
            requireLogin: true,
            requireVerifiedAddress: true,
            runFunction: () => {
                trackBuyNowPressedAnalytics(true);
                dispatch(
                    openBuyNowModal({
                        itemId,
                        modalType: modalTypes.MAKE_OFFER,
                    })
                );
            },
        });
    }, [verifyLogin, trackBuyNowPressedAnalytics, dispatch, itemId]);

    const onViewMoreBuyItNowItemsClicked = useCallback(() => {
        navigate(getCatalogUrl(catalogId, title));
    }, [navigate, catalogId, title]);

    return {
        onBuyNowClicked,
        onMakeAnOfferClicked,
        onViewMoreBuyItNowItemsClicked,
    };
};
